import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import {
  Plus,
  QrCode,
  FileText,
  Link as LinkIcon,
  RefreshCw,
  Copy,
  Check,
  X,
  Send,
  Clock,
  DollarSign,
  AlertTriangle,
} from 'lucide-react';

type ChargeTab = 'create' | 'active' | 'paid' | 'overdue' | 'cancelled';
type ChargeType = 'PIX' | 'BOLETO' | 'PAYMENT_LINK' | 'STATIC_QR' | 'RECURRING';

export function SupplierCharges() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<ChargeTab>('create');
  const [showQrModal, setShowQrModal] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState<any>(null);
  const [copied, setCopied] = useState(false);
  
  // Create charge form state
  const [chargeType, setChargeType] = useState<ChargeType>('PIX');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerDocument, setCustomerDocument] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [expiresIn, setExpiresIn] = useState('3600');
  const [dueDate, setDueDate] = useState('');
  const [recurringInterval, setRecurringInterval] = useState('MONTHLY');

  // Fetch charges by status
  const { data: chargesData, isLoading } = useQuery({
    queryKey: ['supplier', 'charges', activeTab],
    queryFn: async () => {
      const status = activeTab === 'create' ? 'ACTIVE' : activeTab.toUpperCase();
      const response = await api.get('/banking/supplier/charges', { 
        params: { status: status === 'CREATE' ? undefined : status } 
      });
      return response.data;
    },
    enabled: activeTab !== 'create',
  });

  // Create charge mutation
  const createChargeMutation = useMutation({
    mutationFn: async (data: any) => {
      const endpoint = chargeType === 'PIX' ? '/banking/supplier/charges/pix' :
                       chargeType === 'BOLETO' ? '/banking/supplier/charges/boleto' :
                       chargeType === 'PAYMENT_LINK' ? '/banking/supplier/charges/payment-link' :
                       chargeType === 'STATIC_QR' ? '/banking/supplier/charges/static-qr' :
                       '/banking/supplier/charges/recurring';
      const response = await api.post(endpoint, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['supplier', 'charges'] });
      setSelectedCharge(data);
      setShowQrModal(true);
      resetForm();
    },
  });

  // Cancel charge mutation
  const cancelChargeMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(`/banking/supplier/charges/${id}/cancel`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier', 'charges'] });
    },
  });

  // Resend charge mutation
  const resendChargeMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(`/banking/supplier/charges/${id}/resend`);
      return response.data;
    },
  });

  const resetForm = () => {
    setAmount('');
    setDescription('');
    setCustomerName('');
    setCustomerDocument('');
    setCustomerEmail('');
    setExpiresIn('3600');
    setDueDate('');
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatDate = (date: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(date));
  };

  const parseAmount = (value: string): number => {
    const cleaned = value.replace(/\D/g, '');
    return parseInt(cleaned, 10) || 0;
  };

  const handleAmountChange = (value: string) => {
    const numericValue = value.replace(/\D/g, '');
    const cents = parseInt(numericValue, 10) || 0;
    const formatted = (cents / 100).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    setAmount(formatted);
  };

  const handleCreateCharge = () => {
    const amountCents = parseAmount(amount);
    const data: any = {
      amount: amountCents,
      description,
    };

    if (customerName) data.customerName = customerName;
    if (customerDocument) data.customerDocument = customerDocument;
    if (customerEmail) data.customerEmail = customerEmail;

    if (chargeType === 'PIX') {
      data.expiresIn = parseInt(expiresIn, 10);
    } else if (chargeType === 'BOLETO') {
      data.dueDate = dueDate;
    } else if (chargeType === 'RECURRING') {
      data.interval = recurringInterval;
    }

    createChargeMutation.mutate(data);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'bg-blue-500/20 text-blue-400';
      case 'PAID': return 'bg-green-500/20 text-green-400';
      case 'EXPIRED': case 'OVERDUE': return 'bg-red-500/20 text-red-400';
      case 'CANCELLED': return 'bg-surface-500/20 text-surface-400';
      default: return 'bg-surface-500/20 text-surface-400';
    }
  };

  const tabs = [
    { id: 'create' as ChargeTab, label: 'Nova Cobrança', icon: <Plus size={18} /> },
    { id: 'active' as ChargeTab, label: 'Ativas', icon: <Clock size={18} /> },
    { id: 'paid' as ChargeTab, label: 'Pagas', icon: <DollarSign size={18} /> },
    { id: 'overdue' as ChargeTab, label: 'Vencidas', icon: <AlertTriangle size={18} /> },
    { id: 'cancelled' as ChargeTab, label: 'Canceladas', icon: <X size={18} /> },
  ];

  const chargeTypes = [
    { value: 'PIX', label: 'Pix QR Code' },
    { value: 'BOLETO', label: 'Boleto' },
    { value: 'PAYMENT_LINK', label: 'Link de Pagamento' },
    { value: 'STATIC_QR', label: 'QR Code Estático' },
    { value: 'RECURRING', label: 'Cobrança Recorrente' },
  ];

  const expirationOptions = [
    { value: '1800', label: '30 minutos' },
    { value: '3600', label: '1 hora' },
    { value: '7200', label: '2 horas' },
    { value: '86400', label: '24 horas' },
    { value: '259200', label: '3 dias' },
    { value: '604800', label: '7 dias' },
  ];

  const recurringOptions = [
    { value: 'WEEKLY', label: 'Semanal' },
    { value: 'MONTHLY', label: 'Mensal' },
    { value: 'QUARTERLY', label: 'Trimestral' },
    { value: 'YEARLY', label: 'Anual' },
  ];


  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Cobranças</h1>
        <p className="text-surface-400">Crie e gerencie suas cobranças</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap
              transition-colors
              ${activeTab === tab.id
                ? 'bg-primary-500 text-white'
                : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
              }
            `}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Create Charge Tab */}
      {activeTab === 'create' && (
        <Card>
          <div className="p-6 space-y-6">
            <h2 className="text-lg font-semibold text-white">Nova Cobrança</h2>

            {/* Charge Type Selection */}
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Tipo de cobrança
              </label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {chargeTypes.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setChargeType(type.value as ChargeType)}
                    className={`
                      p-3 rounded-lg text-sm font-medium transition-colors text-center
                      ${chargeType === type.value
                        ? 'bg-primary-500 text-white'
                        : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
                      }
                    `}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Valor"
                placeholder="0,00"
                value={amount}
                onChange={(e) => handleAmountChange(e.target.value)}
                leftIcon={<span className="text-surface-400">R$</span>}
              />

              <Input
                label="Descrição"
                placeholder="Ex: Serviço de consultoria"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            {/* Customer Info (optional) */}
            <div className="border-t border-surface-800 pt-6">
              <h3 className="text-sm font-medium text-surface-300 mb-4">
                Dados do cliente (opcional)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="Nome"
                  placeholder="Nome do cliente"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                />
                <Input
                  label="CPF/CNPJ"
                  placeholder="000.000.000-00"
                  value={customerDocument}
                  onChange={(e) => setCustomerDocument(e.target.value)}
                />
                <Input
                  label="E-mail"
                  placeholder="email@exemplo.com"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                />
              </div>
            </div>

            {/* Type-specific options */}
            {chargeType === 'PIX' && (
              <Select
                label="Expiração"
                value={expiresIn}
                onChange={(e) => setExpiresIn(e.target.value)}
                options={expirationOptions}
              />
            )}

            {chargeType === 'BOLETO' && (
              <Input
                label="Data de vencimento"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            )}

            {chargeType === 'RECURRING' && (
              <Select
                label="Intervalo"
                value={recurringInterval}
                onChange={(e) => setRecurringInterval(e.target.value)}
                options={recurringOptions}
              />
            )}

            <Button
              variant="primary"
              className="w-full"
              onClick={handleCreateCharge}
              disabled={!amount || createChargeMutation.isPending}
            >
              {createChargeMutation.isPending ? 'Criando...' : 'Criar Cobrança'}
            </Button>
          </div>
        </Card>
      )}

      {/* Charges List */}
      {activeTab !== 'create' && (
        <Card>
          <div className="p-6">
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center gap-4 p-4 rounded-lg bg-surface-800/50 animate-pulse">
                    <div className="w-12 h-12 rounded-lg bg-surface-700" />
                    <div className="flex-1">
                      <div className="h-4 w-32 bg-surface-700 rounded mb-2" />
                      <div className="h-3 w-48 bg-surface-700 rounded" />
                    </div>
                    <div className="h-6 w-20 bg-surface-700 rounded" />
                  </div>
                ))}
              </div>
            ) : chargesData?.charges?.length > 0 ? (
              <div className="space-y-3">
                {chargesData.charges.map((charge: any) => (
                  <div
                    key={charge.id}
                    className="flex items-center gap-4 p-4 rounded-lg bg-surface-800/50 hover:bg-surface-800 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-lg bg-primary-500/20 flex items-center justify-center">
                      {charge.type === 'PIX' ? <QrCode size={24} className="text-primary-400" /> :
                       charge.type === 'BOLETO' ? <FileText size={24} className="text-primary-400" /> :
                       <LinkIcon size={24} className="text-primary-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">
                        {charge.description || `Cobrança #${charge.correlationId?.slice(-8)}`}
                      </p>
                      <p className="text-xs text-surface-400">
                        {charge.customerName || 'Cliente não informado'} • {formatDate(charge.createdAt)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-white">
                        {formatCurrency(charge.amount)}
                      </p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(charge.status)}`}>
                        {charge.status}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      {charge.status === 'ACTIVE' && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedCharge(charge);
                              setShowQrModal(true);
                            }}
                          >
                            <QrCode size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => resendChargeMutation.mutate(charge.id)}
                          >
                            <Send size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => cancelChargeMutation.mutate(charge.id)}
                          >
                            <X size={16} className="text-red-400" />
                          </Button>
                        </>
                      )}
                      {charge.status === 'EXPIRED' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => resendChargeMutation.mutate(charge.id)}
                        >
                          <RefreshCw size={16} />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText size={48} className="mx-auto text-surface-600 mb-3" />
                <p className="text-surface-400">Nenhuma cobrança encontrada</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* QR Code / Details Modal */}
      <Modal
        isOpen={showQrModal}
        onClose={() => setShowQrModal(false)}
        title="Detalhes da Cobrança"
      >
        {selectedCharge && (
          <div className="space-y-4 text-center">
            {selectedCharge.qrCodeImage && (
              <div className="p-4 bg-white rounded-lg inline-block mx-auto">
                <img
                  src={selectedCharge.qrCodeImage}
                  alt="QR Code"
                  className="w-48 h-48"
                />
              </div>
            )}

            <div className="text-2xl font-bold text-white">
              {formatCurrency(selectedCharge.amount)}
            </div>

            {selectedCharge.description && (
              <p className="text-surface-400">{selectedCharge.description}</p>
            )}

            {selectedCharge.brCode && (
              <div className="p-3 rounded-lg bg-surface-800">
                <p className="text-xs text-surface-400 mb-2">Pix Copia e Cola</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={selectedCharge.brCode}
                    readOnly
                    className="flex-1 bg-transparent text-sm text-white truncate"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(selectedCharge.brCode)}
                  >
                    {copied ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
                  </Button>
                </div>
              </div>
            )}

            {selectedCharge.paymentLink && (
              <div className="p-3 rounded-lg bg-surface-800">
                <p className="text-xs text-surface-400 mb-2">Link de Pagamento</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={selectedCharge.paymentLink}
                    readOnly
                    className="flex-1 bg-transparent text-sm text-white truncate"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(selectedCharge.paymentLink)}
                  >
                    {copied ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
                  </Button>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => setShowQrModal(false)}
              >
                Fechar
              </Button>
              {selectedCharge.customerEmail && (
                <Button
                  variant="primary"
                  className="flex-1"
                  onClick={() => resendChargeMutation.mutate(selectedCharge.id)}
                >
                  <Send size={16} className="mr-2" />
                  Enviar por E-mail
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default SupplierCharges;
